import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Select,
  InputNumber,
  Typography,
  Space,
  Table,
  Spin,
  Statistic,
  Tabs
} from 'antd';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { useTranslation } from 'react-i18next';
import Page from '../../components/layout/Page';
import { API_BASE_URL } from '../../config';
import api from '../../services/api';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

// Modern color palette
const COLORS = {
  primary: '#2C4B39',
  secondary: '#D4E0C9',
  accent1: '#2C4B39',
  accent2: '#2C4B39',
  accent3: '#D4E0C9',
  accent4: '#D4E0C9',
  accent5: '#D4A5A5',
  background: '#F9F9F9',
};

// Formatter functions
const formatEuro = (value) => {
  if (value === undefined || value === null) return '-';
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatPercent = (value) => {
  if (value === undefined || value === null) return '-';
  return `${value.toFixed(2)}%`;
};

const Analytics = () => {
  const [data, setData] = useState([]);
  const [locationData, setLocationData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    location: 'all',
    consultant: 'all',
    year: new Date().getFullYear(),
    compareYear: new Date().getFullYear() - 1,
  });
  const [locations, setLocations] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [analyticsData, setAnalyticsData] = useState({});
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch locations and consultants for filters
    const fetchMetadata = async () => {
      try {
        const [locationsRes, consultantsRes] = await Promise.all([
          api.get('/locations/'),
          api.get('/consultants/'),
        ]);
        setLocations(locationsRes.data);
        setConsultants(consultantsRes.data);
      } catch (error) {
        console.error('Error fetching metadata:', error);
      }
    };

    fetchMetadata();
  }, []);

  useEffect(() => {
    // Fetch analytics data based on filters
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await api.get('/analytics/', {
          params: filters
        });
        
        setData(response.data.monthly_data);
        
        if (response.data.location_totals) {
          const locationStats = response.data.location_totals.map(location => ({
            name: location.primary_consultant__location__name,
            currentYearTotal: location.total_evaluation,
            avgCommission: location.avg_commission,
          }));
          setLocationData(locationStats);
        }
        setAnalyticsData(response.data);
      } catch (error) {
        console.error('Error fetching analytics data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filters]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Calculate total values for current and previous years
  const totalCurrentYear = data.reduce((sum, item) => sum + (item.currentYearConfirmed || 0), 0);
  const totalPreviousYear = data.reduce((sum, item) => sum + (item.previousYearConfirmed || 0), 0);
  const yoyGrowth = totalPreviousYear ? ((totalCurrentYear / totalPreviousYear - 1) * 100).toFixed(1) : 'N/A';

  const renderChart = (data, title, formatter) => {
    const items = [
      {
        key: 'recharts',
        label: 'Recharts Version',
        children: (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip 
                formatter={(value) => formatter(value)}
                labelStyle={{ color: COLORS.primary }}
              />
              <Legend />
              <Bar 
                dataKey="value" 
                fill={COLORS.primary}
                name={title}
              />
            </BarChart>
          </ResponsiveContainer>
        ),
      },
    ];

    return (
      <Card title={title}>
        <Tabs items={items} />
      </Card>
    );
  };

  const renderComparisonChart = (data, year, compareYear) => {
    const items = [
      {
        key: 'evaluation',
        label: t('analytics.charts.evaluation'),
        children: (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip 
                formatter={(value, name) => {
                  if (name.includes(t('analytics.charts.evaluation'))) {
                    return formatEuro(value);
                  }
                  return value;
                }}
                labelFormatter={(label) => `Monat ${label}`}
              />
              <Legend />
              <Bar
                name={`${year} ${t('analytics.charts.evaluation')}`}
                dataKey="currentYearEvaluation"
                fill={COLORS.primary}
              />
              <Bar
                name={`${compareYear} ${t('analytics.charts.evaluation')}`}
                dataKey="previousYearEvaluation"
                fill={COLORS.secondary}
              />
            </BarChart>
          </ResponsiveContainer>
        ),
      },
      {
        key: 'count',
        label: t('analytics.charts.loanCount'),
        children: (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip 
                formatter={(value, name) => [value, name]}
                labelFormatter={(label) => `Monat ${label}`}
              />
              <Legend />
              <Bar
                name={`${year} ${t('analytics.charts.loanCount')}`}
                dataKey="currentYearConfirmed"
                fill={COLORS.primary}
              />
              <Bar
                name={`${compareYear} ${t('analytics.charts.loanCount')}`}
                dataKey="previousYearConfirmed"
                fill={COLORS.secondary}
              />
            </BarChart>
          </ResponsiveContainer>
        ),
      },
    ];

    return (
      <Card title={t('analytics.charts.monthlyComparison')}>
        <Tabs items={items} />
      </Card>
    );
  };

  return (
    <Page
      title={t('analytics.title')}
      subtitle={t('analytics.subtitle')}
      menu={[
        {
          key: 'analytics',
          label: t('analytics.title'),
          path: '/analytics'
        },
        {
          key: 'bank-analytics',
          label: t('analytics.bankAnalytics'),
          path: '/bank-analytics'
        }
      ]}
    >
      <Spin spinning={loading}>
        {/* Filters */}
        <Card style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} sm={6}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text>{t('analytics.filters.location')}</Text>
                <Select
                  style={{ width: '100%' }}
                  value={filters.location}
                  onChange={(value) => handleFilterChange('location', value)}
                >
                  <Option value="all">{t('analytics.filters.allLocations')}</Option>
                  {locations.map((location) => (
                    <Option key={location.id} value={location.id}>
                      {location.name}
                    </Option>
                  ))}
                </Select>
              </Space>
            </Col>
            <Col xs={24} sm={6}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text>{t('analytics.filters.consultant')}</Text>
                <Select
                  style={{ width: '100%' }}
                  value={filters.consultant}
                  onChange={(value) => handleFilterChange('consultant', value)}
                >
                  <Option value="all">{t('analytics.filters.allConsultants')}</Option>
                  {consultants.map((consultant) => (
                    <Option key={consultant.id} value={consultant.id}>
                      {`${consultant.first_name} ${consultant.last_name}`}
                    </Option>
                  ))}
                </Select>
              </Space>
            </Col>
            <Col xs={24} sm={6}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text>{t('analytics.filters.year')}</Text>
                <InputNumber
                  style={{ width: '100%' }}
                  value={filters.year}
                  onChange={(value) => handleFilterChange('year', value)}
                />
              </Space>
            </Col>
            <Col xs={24} sm={6}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text>{t('analytics.filters.compareYear')}</Text>
                <InputNumber
                  style={{ width: '100%' }}
                  value={filters.compareYear}
                  onChange={(value) => handleFilterChange('compareYear', value)}
                />
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Charts */}
        <Row gutter={[16, 16]}>
          {/* Monthly Comparison Chart */}
          <Col span={24}>
            {renderComparisonChart(data, filters.year, filters.compareYear)}
          </Col>

          {/* Location Performance Charts */}
          <Col span={24} lg={12}>
            {renderChart(locationData.map(item => ({ 
              name: item.name, 
              value: item.currentYearTotal 
            })), `${t('analytics.charts.locationPerformance')} (${filters.year})`, formatEuro)}
          </Col>

          {/* Average Commission Chart */}
          <Col span={24} lg={12}>
            {renderChart(locationData.map(item => ({ 
              name: item.name, 
              value: item.avgCommission 
            })), `${t('analytics.charts.averageCommission')} (${filters.year})`, formatPercent)}
          </Col>

          {/* Consultant Rankings */}
          <Col span={24}>
            <Card>
              <Title level={5} style={{ color: COLORS.primary }}>
                {t('analytics.tables.consultantRankings')}
              </Title>
              <Table
                size="small"
                dataSource={analyticsData?.consultant_rankings || []}
                rowKey={(record) => `${record.primary_consultant__first_name}-${record.primary_consultant__last_name}`}
                columns={[
                  {
                    title: t('analytics.tables.columns.rank'),
                    key: 'rank',
                    render: (_, __, index) => index + 1,
                  },
                  {
                    title: t('analytics.tables.columns.name'),
                    key: 'name',
                    render: (_, record) => (
                      <Text strong={record.index === 0}>
                        {`${record.primary_consultant__first_name} ${record.primary_consultant__last_name}`}
                      </Text>
                    ),
                  },
                  {
                    title: t('analytics.tables.columns.location'),
                    dataIndex: 'primary_consultant__location__name',
                    key: 'location',
                  },
                  {
                    title: t('analytics.tables.columns.loans'),
                    dataIndex: 'loan_count',
                    key: 'loans',
                    align: 'right',
                  },
                  {
                    title: t('analytics.tables.columns.avgAmount'),
                    key: 'avgAmount',
                    align: 'right',
                    render: (_, record) => 
                      formatEuro(record.total_amount / record.loan_count),
                  },
                  {
                    title: t('analytics.tables.columns.totalAmount'),
                    dataIndex: 'total_amount',
                    key: 'totalAmount',
                    align: 'right',
                    render: (value) => formatEuro(value),
                  },
                  {
                    title: t('analytics.tables.columns.totalEvaluation'),
                    dataIndex: 'total_evaluation',
                    key: 'totalEvaluation',
                    align: 'right',
                    render: (value) => (
                      <Text style={{ color: COLORS.accent1, fontWeight: 'medium' }}>
                        {formatEuro(value)}
                      </Text>
                    ),
                  },
                  {
                    title: t('analytics.tables.columns.commission'),
                    dataIndex: 'avg_commission',
                    key: 'commission',
                    align: 'right',
                    render: (value) => (
                      <Text style={{ color: COLORS.primary }}>
                        {formatPercent(value)}
                      </Text>
                    ),
                  },
                ]}
                onRow={(record, index) => ({
                  style: {
                    backgroundColor: index === 0 ? `${COLORS.accent1}15` : 'inherit',
                  },
                })}
              />
            </Card>
          </Col>

          {/* Location Rankings */}
          <Col span={24}>
            <Card>
              <Title level={5} style={{ color: COLORS.primary }}>
                {t('analytics.tables.locationRankings')}
              </Title>
              <Table
                size="small"
                dataSource={analyticsData?.location_rankings || []}
                rowKey="primary_consultant__location__name"
                columns={[
                  {
                    title: t('analytics.tables.columns.rank'),
                    key: 'rank',
                    render: (_, __, index) => index + 1,
                  },
                  {
                    title: t('analytics.tables.columns.location'),
                    key: 'location',
                    render: (_, record, index) => (
                      <Text strong={index === 0}>
                        {record.primary_consultant__location__name}
                      </Text>
                    ),
                  },
                  {
                    title: t('analytics.tables.columns.consultants'),
                    dataIndex: 'consultant_count',
                    key: 'consultants',
                    align: 'right',
                  },
                  {
                    title: t('analytics.tables.columns.loans'),
                    dataIndex: 'loan_count',
                    key: 'loans',
                    align: 'right',
                  },
                  {
                    title: t('analytics.tables.columns.loansPerConsultant'),
                    key: 'loansPerConsultant',
                    align: 'right',
                    render: (_, record) => (
                      <Text style={{ color: COLORS.accent1 }}>
                        {(record.loan_count / record.consultant_count).toFixed(1)}
                      </Text>
                    ),
                  },
                  {
                    title: t('analytics.tables.columns.avgAmount'),
                    key: 'avgAmount',
                    align: 'right',
                    render: (_, record) => 
                      formatEuro(record.total_amount / record.loan_count),
                  },
                  {
                    title: t('analytics.tables.columns.totalAmount'),
                    dataIndex: 'total_amount',
                    key: 'totalAmount',
                    align: 'right',
                    render: (value) => formatEuro(value),
                  },
                  {
                    title: t('analytics.tables.columns.totalEvaluation'),
                    dataIndex: 'total_evaluation',
                    key: 'totalEvaluation',
                    align: 'right',
                    render: (value) => (
                      <Text style={{ color: COLORS.accent2, fontWeight: 'medium' }}>
                        {formatEuro(value)}
                      </Text>
                    ),
                  },
                  {
                    title: t('analytics.tables.columns.commission'),
                    dataIndex: 'avg_commission',
                    key: 'commission',
                    align: 'right',
                    render: (value) => (
                      <Text style={{ color: COLORS.primary }}>
                        {formatPercent(value)}
                      </Text>
                    ),
                  },
                ]}
                onRow={(record, index) => ({
                  style: {
                    backgroundColor: index === 0 ? `${COLORS.accent3}15` : 'inherit',
                  },
                })}
                scroll={{ x: true }}
              />
            </Card>
          </Col>
        </Row>
      </Spin>
    </Page>
  );
};

export default Analytics;
